import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Copy, Trash2, Link as LinkIcon, Plus, Globe } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtDate } from "@/lib/format";

export function BudgetShareDialog({ open, onOpenChange, artistId, artistName }) {
  const [shares, setShares] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get(`/budget/artists/${artistId}/shares`).then((r) => setShares(r.data.shares)).catch(() => toast.error("Failed to load share links"));
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, artistId]);

  const create = async () => {
    setCreating(true);
    try { await api.post(`/budget/artists/${artistId}/shares`); toast.success("Share link created"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };
  const copy = (link) => {
    const full = link.startsWith("http") ? link : `${window.location.origin}${link}`;
    navigator.clipboard.writeText(full).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed"));
  };
  const revoke = async (id) => {
    try { await api.delete(`/budget/shares/${id}`); toast.success("Link revoked"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const active = (shares || []).filter((s) => !s.revoked);
  const revoked = (shares || []).filter((s) => s.revoked);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="budget-share-dialog" className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-[#35606E]" /> Share {artistName}’s budget</DialogTitle>
          <DialogDescription>Read-only links to this artist’s budget only. Anyone with a link can view it — no login needed. Revoke to kill a link instantly.</DialogDescription>
        </DialogHeader>

        <Button data-testid="create-share-btn" onClick={create} disabled={creating} className="w-full rounded-full bg-[#35606E] hover:bg-[#294b57]">
          {creating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Create share link
        </Button>

        {shares === null && <div className="grid h-20 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>}

        {shares && active.length === 0 && <p className="py-2 text-center text-sm text-neutral-500">No active share links yet.</p>}

        {active.length > 0 && (
          <div className="flex flex-col gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Active ({active.length})</p>
            {active.map((s) => (
              <div key={s.id} data-testid={`share-${s.id}`} className="flex items-center justify-between gap-2 rounded-xl border border-[#E7DFCC] bg-[#FFFDF8] p-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-1.5 truncate text-sm font-medium"><LinkIcon className="h-3.5 w-3.5 text-[#35606E]" /> {s.link}</p>
                  <p className="text-xs text-neutral-500">Created {fmtDate(s.created_at)}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button data-testid={`copy-share-${s.id}`} onClick={() => copy(s.link)} variant="outline" size="sm" className="rounded-full"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
                  <button data-testid={`revoke-share-${s.id}`} onClick={() => revoke(s.id)} className="rounded-full p-2 text-neutral-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {revoked.length > 0 && (
          <div className="flex flex-col gap-1 opacity-60">
            <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Revoked ({revoked.length})</p>
            {revoked.map((s) => (
              <p key={s.id} className="truncate text-xs text-neutral-400 line-through">{s.link}</p>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
